'use client';

import React, { useState, useCallback } from 'react';
import { useSoundboard } from '@/contexts/SoundboardManager';
import { SoundboardItem as SoundboardItemType } from '@/utils/soundboardStorage';
import SortableSoundboardItem from './SortableSoundboardItem';
import { FaChevronDown, FaChevronUp, FaMusic, FaTrash, FaInfoCircle } from 'react-icons/fa';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface SoundboardPanelProps {
  onPlaySound: (item: SoundboardItemType) => void;
  className?: string;
}

const SoundboardPanel: React.FC<SoundboardPanelProps> = ({
  onPlaySound,
  className = ''
}) => {
  const {
    items,
    isExpanded,
    isLoading,
    error,
    toggleExpanded,
    getStorageStats,
    clearAll,
    reorderItems
  } = useSoundboard();

  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const [showStorageInfo, setShowStorageInfo] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handlePlaySound = useCallback((item: SoundboardItemType) => {
    console.log('🎵 SoundboardPanel handlePlaySound called with:', {
      itemName: item.name,
      itemId: item.id,
      hasOnPlaySound: !!onPlaySound
    });
    
    setCurrentlyPlaying(item.id);
    onPlaySound(item);
    
    // Reset playing state after a delay (you might want to listen to actual audio events)
    setTimeout(() => {
      setCurrentlyPlaying(null);
    }, 2000);
  }, [onPlaySound]);

  const handleClearAll = useCallback(async () => {
    if (window.confirm('Are you sure you want to clear all sounds from the soundboard?')) {
      clearAll();
    }
  }, [clearAll]);

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex(item => item.id === active.id);
      const newIndex = items.findIndex(item => item.id === over.id);
      
      const reorderedItems = arrayMove(items, oldIndex, newIndex);
      await reorderItems(reorderedItems);
    }
  }, [items, reorderItems]);

  const formatStorageSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const storageStats = getStorageStats();

  if (isLoading) {
    return (
      <div className={`bg-slate-800 rounded-lg border border-slate-700 p-4 ${className}`}>
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          <span className="ml-2 text-slate-400">Loading soundboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-slate-800 rounded-lg border border-slate-700 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-slate-700">
        <div className="flex items-center space-x-2">
          <FaMusic className="w-4 h-4 text-blue-400" />
          <span className="text-sm font-semibold text-white">
            Soundboard ({items.length})
          </span>
        </div>
        
        <div className="flex items-center space-x-2">
          {/* Storage Info Button */}
          <button
            onClick={() => setShowStorageInfo(!showStorageInfo)}
            className="p-1 rounded hover:bg-slate-700 transition-colors"
            title="Storage Information"
          >
            <FaInfoCircle className="w-3 h-3 text-slate-400" />
          </button>
          
          {/* Clear All Button */}
          {items.length > 0 && (
            <button
              onClick={handleClearAll}
              className="p-1 rounded hover:bg-red-700 transition-colors"
              title="Clear All Sounds"
            >
              <FaTrash className="w-3 h-3 text-red-400" />
            </button>
          )}
          
          {/* Toggle Button */}
          <button
            onClick={toggleExpanded}
            className="p-1 rounded hover:bg-slate-700 transition-colors"
            title={isExpanded ? 'Collapse soundboard' : 'Expand soundboard'}
          >
            {isExpanded ? (
              <FaChevronUp className="w-3 h-3 text-slate-400" />
            ) : (
              <FaChevronDown className="w-3 h-3 text-slate-400" />
            )}
          </button>
        </div>
      </div>

      {/* Storage Info */}
      {showStorageInfo && (
        <div className="px-3 py-2 bg-slate-900 border-b border-slate-700">
          <div className="text-xs text-slate-400 space-y-1">
            <div>Storage: {formatStorageSize(storageStats.totalSize)} / {formatStorageSize(storageStats.maxSize)}</div>
            <div>Items: {storageStats.itemCount} / {storageStats.maxItems}</div>
            <div className="w-full bg-slate-700 rounded-full h-1.5">
              <div
                className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${(storageStats.totalSize / storageStats.maxSize) * 100}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="px-3 py-2 bg-red-900/20 border-b border-red-500/30">
          <div className="text-xs text-red-300">{error}</div>
        </div>
      )}

      {/* Content */}
      {isExpanded && (
        <div className="p-3">
          {items.length === 0 ? (
            <div className="text-center py-8">
              <FaMusic className="w-8 h-8 text-slate-600 mx-auto mb-2" />
              <p className="text-sm text-slate-400 mb-1">No sounds in soundboard</p>
              <p className="text-xs text-slate-500">
                Drag and drop audio files here to add them
              </p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={items.map(item => item.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {items
                    .sort((a, b) => a.order - b.order)
                    .map((item) => (
                      <SortableSoundboardItem
                        key={item.id}
                        item={item}
                        onPlay={handlePlaySound}
                        isPlaying={currentlyPlaying === item.id}
                      />
                    ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      )}

      {/* Collapsed Preview */}
      {!isExpanded && items.length > 0 && (
        <div className="px-3 py-2">
          <div className="flex items-center space-x-2 text-xs text-slate-400">
            <span>Recent sounds:</span>
            <div className="flex space-x-1">
              {items
                .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
                .slice(0, 3)
                .map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handlePlaySound(item)}
                    className="px-2 py-1 bg-slate-700 rounded hover:bg-slate-600 transition-colors truncate max-w-20"
                    title={item.name}
                  >
                    {item.name}
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SoundboardPanel;
